import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import SetupEntry from '@/pages/shared/SetupEntry.vue'
import PackageSelection from '@/pages/shared/PackageSelection.vue'
import PackageLogin from '@/pages/shared/PackageLogin.vue'
import SetupSuccess from '@/pages/shared/SetupSuccess.vue'
import ActivateQbo from '@/components/integrations/qbo/ActivateQbo.vue'
import QboSuccess from '@/components/integrations/qbo/QboSuccess.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import DashboardView from '@/pages/app/DashboardView.vue'
import TasksView from '@/pages/app/TasksView.vue'
import CustomersView from '@/pages/app/CustomersView.vue'
import ItemsView from '@/pages/app/ItemsView.vue'
import InvoicesView from '@/pages/app/InvoicesView.vue'
import PaymentsView from '@/pages/app/PaymentsView.vue'
import LedgersView from '@/pages/app/LedgersView.vue'
import SettingsView from '@/pages/app/SettingsView.vue'
import UserLogin from '@/pages/shared/UserLogin.vue'
import StoreSelection from '@/pages/shared/StoreSelection.vue'
import LoginProviderSelection from '@/pages/shared/LoginProviderSelection.vue'
import InvoiceAccess from '@/pages/shared/InvoiceAccess.vue'
import { useAuthStore } from '@/stores/authStore'
import { track } from '@/lib/usageTracker'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      const auth = useAuthStore()
      // Check for any authenticated integration directly — does not depend on selectedPackageId
      const hasAuthIntegration = Object.values(auth.integrations).some(i => i.status === 'authenticated')
      return hasAuthIntegration ? '/app/dashboard' : '/login'
    },
  },
  {
    path: '/setup',
    name: 'SetupEntry',
    component: SetupEntry,
  },
  {
    path: '/setup/packages',
    name: 'PackageSelection',
    component: PackageSelection,
    meta: { requiresAddon: true },
  },
  {
    path: '/setup/packages/:packageId/login',
    name: 'PackageLogin',
    component: PackageLogin,
    meta: { requiresAddon: true },
  },
  {
    path: '/setup/success',
    name: 'SetupSuccess',
    component: SetupSuccess,
    meta: { requiresAddon: true },
  },
  {
    path: '/integrations/qbo/activate',
    name: 'ActivateQbo',
    component: ActivateQbo,
  },
  {
    path: '/integrations/qbo/callback',
    name: 'QboSuccess',
    component: QboSuccess,
  },
  // --- Store-login routes ---
  {
    path: '/login',
    name: 'UserLogin',
    component: UserLogin,
  },
  {
    path: '/setup/stores',
    name: 'StoreSelection',
    component: StoreSelection,
    meta: { requiresUser: true },
  },
  {
    path: '/setup/providers',
    name: 'LoginProviderSelection',
    component: LoginProviderSelection,
    meta: { requiresUser: true, requiresStore: true },
  },
  {
    path: '/setup/invoice-access',
    name: 'InvoiceAccess',
    component: InvoiceAccess,
    meta: { requiresUser: true, requiresStore: true },
  },
  {
    path: '/app',
    component: MainLayout,
    children: [
      { path: '', redirect: '/app/dashboard' },
      { path: 'dashboard', name: 'Dashboard', component: DashboardView, meta: { requiresAuth: true } },
      { path: 'tasks', name: 'Tasks', component: TasksView, meta: { requiresAuth: true } },
      { path: 'customers', name: 'Customers', component: CustomersView, meta: { requiresAuth: true } },
      { path: 'items', name: 'Items', component: ItemsView, meta: { requiresAuth: true } },
      { path: 'invoices', name: 'Invoices', component: InvoicesView, meta: { requiresAuth: true } },
      { path: 'payments', name: 'Payments', component: PaymentsView, meta: { requiresAuth: true } },
      { path: 'ledgers', name: 'Ledgers', component: LedgersView, meta: { requiresAuth: true } },
      { path: 'settings', name: 'Settings', component: SettingsView, meta: { requiresAuth: true } },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach((to, from) => {
  const auth = useAuthStore()

  if (to.name === 'SetupEntry') return true

  if (to.name === 'ActivateQbo' || to.name === 'QboSuccess') return true

  if (to.meta.requiresAddon) {
    const hasApiKey = !!(to.query.apiKey && to.query.realmId)
    const hasParams = !!(to.query.invoiceNumber && to.query.storeID)
    if (!hasParams && !hasApiKey) {
      const query = Object.keys(to.query).length ? to.query : from.query
      return { name: 'SetupEntry', query }
    }
    if (!auth.invoiceHasAddon && !hasApiKey) {
      return { name: 'SetupEntry', query: to.query }
    }
  }

  // Store-login gate: only authenticated users (from store login) may
  // continue into store selection. Existing query-param setup links
  // still bypass this by hitting /setup/* directly.
  if (to.meta.requiresUser && !auth.hasUserContext) {
    return { name: 'UserLogin', query: to.query }
  }
  if (to.meta.requiresStore && !auth.hasSelectedStore) {
    return { name: 'StoreSelection', query: to.query }
  }

  if (to.meta.requiresAuth) {
    // Check for any authenticated integration directly — does not depend on selectedPackageId
    const hasAuthIntegration = Object.values(auth.integrations).some(i => i.status === 'authenticated')
    if (!hasAuthIntegration) {
      // If the user has logged in but has not selected a store yet,
      // route them through the store-login flow. Otherwise back to /login.
      if (auth.hasUserContext) {
        return { name: 'StoreSelection', query: to.query || from.query }
      }
      return { name: 'UserLogin', query: to.query || from.query }
    }
  }

  return true
})
router.afterEach((to) => {
  if (to.name === undefined) return
  track(`page:${String(to.name)}`, 'view', { keepalive: true })
})


export default router
